<div class="min-h-screen py-4 bg-neutral-50">
  <div class="max-w-5xl mx-auto px-4">
    <div *ngIf="!isFormReady" class="flex items-center justify-center py-20">
      <div class="text-center">
        <mat-spinner diameter="40"></mat-spinner>
        <p class="mt-4 text-sm text-neutral-600">Loading form...</p>
      </div>
    </div>

    <div *ngIf="isFormReady">
      <div class="relative mb-4 p-4 rounded-xl bg-white border border-neutral-200 shadow-sm">
        <div class="flex items-center justify-between flex-wrap gap-3">
          <div class="flex items-center gap-2.5">
            <div
              class="flex items-center justify-center w-10 h-10 bg-primary-600 text-white rounded-lg"
            >
              <mat-icon class="!text-[20px] !w-5 !h-5">assignment</mat-icon>
            </div>
            <div>
              <h1 class="text-lg font-bold text-neutral-900">{{ formName }}</h1>
            </div>
          </div>

          <div
            *ngIf="visibleSteps.length > 1"
            class="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-100 rounded-full border border-neutral-200"
          >
            <span class="text-xs font-bold text-neutral-900">Step {{ currentStepIndex + 1 }}</span>
            <span class="text-neutral-400 text-xs">/</span>
            <span class="text-xs text-neutral-600">{{ visibleSteps.length }}</span>
          </div>
        </div>

        <!-- Progress stepper -->
        <div *ngIf="visibleSteps.length > 1" class="relative mt-3 flex items-center justify-between">
          <div class="absolute top-1/2 left-0 right-0 h-px bg-neutral-200 -translate-y-1/2"></div>
          <div
            class="absolute top-1/2 left-0 h-px bg-primary-600 -translate-y-1/2 transition-all duration-500"
            [style.width.%]="((currentStepIndex + 1) / visibleSteps.length) * 100"
          ></div>

          <div
            *ngFor="let step of visibleSteps; let i = index"
            tabindex="0"
            role="button"
            [attr.aria-label]="'Go to step ' + (i + 1) + ': ' + step.title"
            class="relative z-10 flex flex-col items-center group cursor-pointer"
            (click)="i < currentStepIndex && (stepper.selectedIndex = i)"
            matTooltip="{{ step.title }}"
            matTooltipPosition="above"
          >
            <div
              class="flex items-center justify-center w-7 h-7 rounded-full border-2 transition-all duration-300"
              [ngClass]="{
                'bg-primary-600 border-primary-600': i <= currentStepIndex,
                'bg-white border-neutral-300': i > currentStepIndex
              }"
            >
              <mat-icon
                class="!text-[14px] !w-3.5 !h-3.5 transition-all duration-300"
                [ngClass]="{
                  'text-white': i <= currentStepIndex,
                  'text-neutral-400': i > currentStepIndex
                }"
              >
                {{ i < currentStepIndex ? 'check' : 'radio_button_unchecked' }}
              </mat-icon>
            </div>
          </div>
        </div>
      </div>

      <ng-container *ngIf="isFormReady">
        <mat-horizontal-stepper
          linear
          #stepper
          class="modern-stepper"
          (selectionChange)="onStepChange($event)"
        >
          <ng-container *ngFor="let step of steps; let i = index">
            <mat-step *ngIf="isStepVisible(step, i)" [stepControl]="stepGroups[i]">
              <form [formGroup]="stepGroups[i]">
                <div
                  class="rounded-xl bg-white border border-neutral-200 shadow-sm"
                  [class.p-5]="step.fields.length > 0"
                  [class.p-0]="step.fields.length === 0"
                >
                  <div *ngIf="step.fields.length > 0" class="mb-4 pb-3 border-b border-neutral-200">
                    <div class="flex items-start gap-2.5">
                      <div
                        class="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg bg-primary-600 text-white"
                      >
                        <mat-icon class="!text-[18px] !w-4.5 !h-4.5">{{ getStepIcon(i) }}</mat-icon>
                      </div>
                      <div class="flex-1">
                        <h2 class="text-base font-bold text-neutral-900">{{ step.title }}</h2>
                        <p class="text-xs text-neutral-600 mt-0.5">
                          {{ step.subtitle || 'Please fill in all required fields to continue' }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <mat-card-content
                    class="form-grid px-0"
                    [ngStyle]="{
                      'grid-template-columns': 'repeat(' + columns + ', minmax(0, 1fr))',
                      gap: '1rem'
                    }"
                  >
                    <!-- Always show summary on last step -->
                    <ng-container *ngIf="i === steps.length - 1">
                      <div class="span-3 mb-6">
                        <app-form-summary
                          [steps]="visibleSteps"
                          [stepGroups]="stepGroups"
                          [formName]="formName"
                          [formSelectData]="formSelectData"
                        ></app-form-summary>
                      </div>
                    </ng-container>

                    <ng-container *ngFor="let field of step.fields">
                      <div
                        *ngIf="isFieldVisible(field, i)"
                        [ngClass]="getColSpanClass(field)"
                        class="transition-all duration-300 ease-in-out"
                      >
                        <app-inputs
                          [field]="field"
                          [group]="stepGroups[i]"
                          [referenceNumber]="getReferenceNumber()"
                          [formSelectData]="formSelectData"
                          (validatedDataChange)="validated.emit($event)"
                        ></app-inputs>
                      </div>
                    </ng-container>

                    <!-- Action buttons -->
                  </mat-card-content>
                  <div
                    class="flex items-center justify-between mt-5 mb-5 pr-5 pl-5 pt-4 border-t border-neutral-200"
                  >
                    <div>
                      <button
                        type="button"
                        matStepperPrevious
                        *ngIf="i !== 0"
                        class="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors font-medium text-sm"
                      >
                        <mat-icon class="!text-[18px] !w-4.5 !h-4.5">arrow_back</mat-icon>
                        <span>Previous</span>
                      </button>
                    </div>

                    <div class="flex items-center gap-2 flex-end">
                      <button
                        type="button"
                        (click)="onNextStep(i)"
                        *ngIf="i < steps.length - 1"
                        class="inline-flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
                      >
                        <span>Continue</span>
                        <mat-icon class="!text-[18px] !w-4.5 !h-4.5">arrow_forward</mat-icon>
                      </button>

                      <button
                        type="button"
                        (click)="onSubmitClick()"
                        *ngIf="i === steps.length - 1 && !hideSubmit"
                        [disabled]="isLoading"
                        class="inline-flex items-center flex-end gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 font-medium text-sm shadow-sm"
                      >
                        <mat-spinner *ngIf="isLoading" diameter="20"></mat-spinner>
                        <ng-container *ngIf="!isLoading">
                          <mat-icon>send</mat-icon>
                        </ng-container>
                        <span>
                          <ng-container *ngIf="isEditMode; else submitLabel">Resubmit Application</ng-container>
                          <ng-template #submitLabel><span *ngIf="isPaymentRequired">Pay and </span>Submit Application</ng-template>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </mat-step>
          </ng-container>
        </mat-horizontal-stepper>
      </ng-container>
      <!-- End of isFormReady wrapper -->
    </div>
    <!-- End of isFormReady div wrapper -->
  </div>
</div>
